import { ScheduleType } from '@core/referential/schedule-type.enum'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

export const CTA_TAB_VALUE = 'cta' as const
export const VRV_TAB_VALUE = 'vrv' as const
export const HEATED_FLOOR_TAB_VALUE = 'heatedFloor' as const

const TabSchema = z.union([z.nativeEnum(ScheduleType), z.literal(CTA_TAB_VALUE), z.literal(VRV_TAB_VALUE), z.literal(HEATED_FLOOR_TAB_VALUE)])

type Tab = z.infer<typeof TabSchema>

// Chaque entrée « Calendrier » de la sidebar n'expose qu'une famille d'onglets ; `family` est
// normalisée avec `tab` dans le transform pour que `search.tab` soit TOUJOURS l'onglet rendu.
export const FAMILY_TABS = {
  general: [ScheduleType.HEATING, VRV_TAB_VALUE, HEATED_FLOOR_TAB_VALUE],
  cta: [CTA_TAB_VALUE],
  lighting: [ScheduleType.LIGHTING],
} as const satisfies Record<string, ReadonlyArray<Tab>>

export type SchedulerFamily = keyof typeof FAMILY_TABS

// Discriminant partagé avec les sous-routes du scheduler (création, édition, CTA) : c'est lui qui dit à
// la sidebar laquelle de ses trois entrées « Calendrier » est ouverte, toutes trois pointant vers la
// même route et ne différant que par leur search.
export const FamilySchema = z.enum(['general', 'cta', 'lighting'])

const CTA_TABS: ReadonlySet<Tab> = new Set([
  CTA_TAB_VALUE,
  ScheduleType.CTA_SPEED,
  ScheduleType.CTA_PRESSURE,
  ScheduleType.CTA_CO2,
  ScheduleType.CTA_RETURN_TEMP_SETPOINT,
])

export const familyOfTab = (tab: Tab): SchedulerFamily => {
  if (CTA_TABS.has(tab)) return 'cta'

  if (tab === ScheduleType.LIGHTING) return 'lighting'

  return 'general'
}

export const SearchSchema = z
  .object({
    family: FamilySchema.optional(),
    tab: TabSchema.optional(),
    tableType: z.union([z.literal('schedules'), z.literal('spaces')]).default('schedules'),
  })
  .transform((search) => {
    // Deep-links historiques (`?tab=cta` sans `family`) : la famille est dérivée de l'onglet.
    const family = search.family ?? (search.tab ? familyOfTab(search.tab) : 'general')
    const familyTabs: ReadonlyArray<Tab> = FAMILY_TABS[family]
    const tab = search.tab && familyTabs.includes(search.tab) ? search.tab : familyTabs[0]

    return { family, tab, tableType: search.tableType }
  })

export type Search = z.infer<typeof SearchSchema>

export const Route = createFileRoute('/_dashboard/site/$id/operation/scheduler/')({
  validateSearch: SearchSchema,
})
